<script lang="ts" setup>
import type { AdminCategoryResponse } from "mws-catalog-ts-sdk";
import { CategoryType } from "mws-catalog-ts-sdk-v2-admin";
import { useRouter } from "vue-router";

import BaseButton from "@/common/components/BaseButton.vue";
import FormKitDropdown from "@/common/components/FormKitDropdown.vue";
import BaseIcon from "@/common/components/icons/BaseIcon.vue";
import PageWrapper from "@/common/components/PageWrapper.vue";
import RouterLinkVue from "@/common/components/RouterLinkVue.vue";
import PaginatedTable from "@/common/components/table/PaginatedTable.vue";
import type { TableColumn } from "@/common/components/table/types";
import { startCase } from "@/common/utils/startCase";

import { useCategoriesCollectionQuery } from "../api/queries";

definePage({
  meta: {
    auth: ["Categories.Read"],
  },
});

const tabs = [
  {
    title: "Categories",
    to: "/categories",
  },
  {
    title: "Menu Hierarchies",
    to: "/menu-hierarchies",
  },
];

const tableColumns: TableColumn<AdminCategoryResponse>[] = [
  {
    key: "category",
    label: "name",
    type: "slot",
  },
  {
    key: "dateCreated",
    label: "Created at",
    type: "date",
  },
  {
    key: "dateModified",
    label: "Last modified at",
    type: "date",
  },
];

const statuses = Object.values(CategoryType).map((type) => ({
  label: startCase(type),
  value: type,
}));

const router = useRouter();
const onCreateClick = () => {
  router.push("/categories/create");
};
</script>

<template>
  <PageWrapper
    title="Categories"
    :breadcrumb-links="[
      {
        label: 'Categories',
        to: '#',
      },
    ]"
    :tabs="tabs"
  >
    <template #actions>
      <BaseButton
        variant="primary"
        label="Create"
        data-test="create-category-button"
        @click="onCreateClick"
      />
    </template>
    <PaginatedTable
      :collection-query-fn="useCategoriesCollectionQuery"
      :columns="tableColumns"
      :is-searchable="true"
      data-test="paginated-table"
    >
      <template #filters="{ filters }">
        <FormKitDropdown
          v-model="filters.type"
          data-test="categories-type-filter"
          name="externalReference.type"
          placeholder="Category Type"
          input-class="min-w-[8rem]"
          :options="statuses"
        />
      </template>
      <template #category="{ item }">
        <RouterLinkVue
          :to="`/categories/${item.id}/details`"
          class="flex items-center gap-x-3"
          data-test="category-link"
        >
          <div class="h-10 aspect-square flex justify-center">
            <img
              v-if="item.logoUrl"
              :src="item.logoUrl"
              class="object-contain"
              data-test="category-logo"
            />
            <BaseIcon
              v-else
              name="heroicons:swatch"
              class="size-full p-1 text-indigo-400"
              data-test="category-icon-fallback"
            />
          </div>
          <span class="flex flex-col justify-start">
            <span data-test="category-name">{{ item.name }}</span>
            <span
              v-if="item.externalReference?.type"
              class="text-indigo-400"
              data-test="category-type"
            >
              {{ startCase(item.externalReference.type) }}
            </span>
          </span>
        </RouterLinkVue>
      </template>
    </PaginatedTable>
  </PageWrapper>
</template>
